import type React from "react";

import { cn } from "@/lib/utils";

import { type VariantProps, cva } from "class-variance-authority";

export const headerTextVariants = cva(
  "m-0 font-bold tracking-1.5 cursor-default",
  {
    variants: {
      size: {
        xxxl: "text-heading-xxxl leading-58",
        xxl: "text-heading-xxl leading-39",
        xl: "text-heading-xl leading-29",
        lg: "text-heading-lg leading-24",
        md: "text-heading-md leading-19",
        sm: "text-heading-sm leading-15",
      },
      textColor: {
        primary: "text-primary",
        secondary: "text-secondary",
        tertiary: "text-tertiary",
        warning: "text-warning",
        info: "text-info",
        success: "text-success",
        error: "text-error",
        link: "text-link",
        superclinical: "text-superclinical",
      },
      italic: {
        true: "italic",
      },
      underline: {
        true: "underline",
      },
      lineThrough: {
        true: "line-through",
      },
      wrap: {
        false: "whitespace-nowrap", // collapses whitespace and line breaks
      },
      preserveLineBreaks: {
        true: "whitespace-pre-wrap", // preserves whitespace and line breaks
      },
      /**
       * Truncate text with ellipsis. It adds the following CSS properties:
       * overflow: hidden;
       * text-overflow: ellipsis;
       * white-space: nowrap;
       */
      truncate: {
        true: "truncate",
      },
    },
    defaultVariants: {
      size: "md",
      textColor: "primary",
      italic: false,
      underline: false,
      lineThrough: false,
      truncate: false,
      preserveLineBreaks: false,
    },
  },
);

interface HeaderTextProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof headerTextVariants> {
  tag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
}

/**
 * Renders a header text component with customizable variants.
 *
 * @component
 * @param {object} props - The component props.
 * @param {string} [className] - Additional CSS class name for the component.
 * @param {string} [tag] - The HTML tag to be used for the header text.
 * @param {string} [size] - The size variant of the header text.
 * @param {string} [textColor] - The text color variant of the header text.
 * @param {boolean} [italic] - Whether the header text should be italicized.
 * @param {boolean} [underline] - Whether the header text should be underlined.
 * @param {boolean} [lineThrough] - Whether the header text should have a line through it.
 * @param {boolean} [preserveLineBreaks] - Whether the header text should preserve line breaks.
 * @param {boolean} [truncate] - Whether the header text should be truncated.
 * @param {string} [wrap] - Whether the header text should wrap or not.
 * @returns {JSX.Element} The rendered header text component.
 */

const HeaderText: React.FC<HeaderTextProps> = ({
  className,
  tag = "h1",
  size,
  textColor,
  italic,
  underline,
  lineThrough,
  preserveLineBreaks,
  truncate,
  wrap,
  ...props
}) => {
  const Comp = tag;
  return (
    <Comp
      className={cn(
        headerTextVariants({
          size,
          textColor,
          italic,
          underline,
          lineThrough,
          truncate,
          preserveLineBreaks,
          wrap,
          className,
        }),
      )}
      {...props}
    />
  );
};

export { HeaderText };
